import React, { useCallback, useMemo, useState } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { PresetViewScope } from '@bringg/types';
import { Button, EditableTitle, Modal, Tooltip } from '@bringg/react-components';
import { BringgIcon, BringgFontIcons } from '@bringg/bringg-icons';
import { confirm } from '@bringg/react-components/dist/components/modal/modal';

import { ClientSideConfig } from '../stores/stores/types';
import { getPresetIcon } from '../utils/preset-views-service';
import notification from 'bringg-web/services/notification';
import { useStores } from 'bringg-web/recipes';

import styles from './manage-preset-views.module.scss';

const ModalTitle = ({ text }: { text: string }) => {
	return <div className={styles.modalTitle}>{text}</div>;
};

type ListItemProps = {
	presetView: ClientSideConfig;
	isActive: boolean;
	activateDefaultPresetView: () => Promise<void>;
};

const ListItem = ({ presetView, isActive, activateDefaultPresetView }: ListItemProps) => {
	const { t } = useTranslation();
	const {
		usersStore: { currentUser }
	} = useStores();
	const [newPresetName, setNewPresetName] = useState(presetView.name);

	const handleApplyName = useCallback(async () => {
		if (!newPresetName?.trim().length) {
			notification.warn(t('PRESET_VIEWS.MANAGE_DISPLAYS.NAME_CANNOT_BE_EMPTY'));
			setNewPresetName(presetView.name);
			return;
		}

		if (newPresetName === presetView.name) {
			return;
		}

		try {
			await presetView.changePresetName(newPresetName);
		} catch (error) {
			setNewPresetName(presetView.name);
			notification.error(t('PRESET_VIEWS.MANAGE_DISPLAYS.FAILED_TO_CHANGE_PRESET_NAME'));
		}
	}, [presetView, newPresetName, t]);

	const handleNameChanged = useCallback((newName: string) => setNewPresetName(newName), [presetView]);

	const openConfirmationModal = useCallback(
		(title: string, action: (args?: any) => Promise<void>, args?: any) => {
			confirm({
				title,
				async onOk() {
					return action(args);
				},
				okButtonProps: { type: 'primary', className: 'bringg-btn' }, // todo: btn class should be applied in @bringg/react-component
				cancelButtonProps: { type: 'link', className: 'bringg-btn' } // todo: btn class should be applied in @bringg/react-component
			});
		},
		[t]
	);

	const handleDelete = useCallback(() => {
		openConfirmationModal(
			t('PRESET_VIEWS.MANAGE_DISPLAYS.DELETE_CONFIRMATION', { presetName: presetView.name }),
			async () => {
				try {
					await presetView.deletePreset();

					if (isActive) {
						await activateDefaultPresetView();
					}
				} catch (e) {
					console.error(e);
				}
			},
			isActive // when deleting active preset the default merchant lvl preset should be activated for view
		);
	}, [presetView, isActive, activateDefaultPresetView, openConfirmationModal]);

	const handlePromote = useCallback(() => {
		openConfirmationModal(
			t('PRESET_VIEWS.MANAGE_DISPLAYS.PROMOTE_CONFIRMATION', { presetName: presetView.name }),
			presetView.promoteToMerchantLevel
		);
	}, [presetView, openConfirmationModal]);

	const tooltipOffset = useMemo(() => ({ offset: [0, 10] }), []);

	const nameTooltip = presetView.edited
		? t('PRESET_VIEWS.MANAGE_DISPLAYS.SAVE_PRESET_BEFORE_APPLY_CHANGES_TOOLTIP')
		: t('PRESET_VIEWS.MANAGE_DISPLAYS.RENAME_TOOLTIP');

	const promoteTooltip = presetView.edited
		? t('PRESET_VIEWS.MANAGE_DISPLAYS.SAVE_PRESET_BEFORE_APPLY_CHANGES_TOOLTIP')
		: t('PRESET_VIEWS.MANAGE_DISPLAYS.PROMOTE_TOOLTIP');

	const actionsAllowed = useMemo(() => {
		if (currentUser.admin) {
			return true;
		}

		return presetView.scope === PresetViewScope.User;
	}, []);

	return (
		<div className={styles.listItem}>
			<div className={styles.leftColumn}>
				<BringgIcon className={styles.scopeIcon} iconName={getPresetIcon(presetView.scope)} />
				<Tooltip align={tooltipOffset} placement="top" title={actionsAllowed ? nameTooltip : ''}>
					<div className={styles.presetName}>
						{presetView.edited || !actionsAllowed ? (
							<span className={styles.truncate}>{presetView.name}</span>
						) : (
							<EditableTitle
								tooltip={false}
								className={styles.nameInput}
								value={newPresetName}
								onApplyChanges={handleApplyName}
								onValueChange={handleNameChanged}
								maxLength={34}
								maxEditLength={100}
							/>
						)}
					</div>
				</Tooltip>
			</div>
			<div className={styles.rightColumn}>
				{presetView.isDefault ? (
					<span className={styles.defaultLabel}>{t('PRESET_VIEWS.MANAGE_DISPLAYS.DEFAULT')}</span>
				) : null}
				{isActive ? (
					<span className={styles.activeLabel}>{t('PRESET_VIEWS.MANAGE_DISPLAYS.ACTIVE')}</span>
				) : null}
				<div className={styles.actionsContainer}>
					{presetView.scope === PresetViewScope.User && currentUser.admin ? (
						<Tooltip align={tooltipOffset} placement="top" title={promoteTooltip}>
							<div>
								<Button
									type="link"
									shape="circle"
									disabled={presetView.edited || presetView.loading}
									onClick={handlePromote}
								>
									<BringgIcon iconName={BringgFontIcons.Monitor} />
								</Button>
							</div>
						</Tooltip>
					) : null}
					<Button
						type="link"
						shape="circle"
						disabled={presetView.isDefault || presetView.loading || !actionsAllowed}
						onClick={handleDelete}
					>
						<BringgIcon iconName={BringgFontIcons.Trash} />
					</Button>
				</div>
			</div>
		</div>
	);
};

interface Props {
	visible: boolean;
	presetViews: ClientSideConfig[];
	activePreset: ClientSideConfig;
	onClose: () => void;
}

export const ManagePresetViews = observer(({ visible, onClose, presetViews: presetViewItems, activePreset }: Props) => {
	const { t } = useTranslation();

	const footer = useMemo(
		() => [
			<Button key="submit" type="primary" onClick={onClose}>
				{t('PRESET_VIEWS.MANAGE_DISPLAYS.DONE')}
			</Button>
		],
		[t, onClose]
	);

	const activateDefaultPreset = useCallback(async () => {
		return presetViewItems
			?.find(({ isDefault }) => isDefault)
			?.activatePresetForView()
			.then(() => {
				notification.success(t('PRESET_VIEWS.MANAGE_DISPLAYS.SWITCHED_TO_MERCHANT_DEFAULT_PRESET'));
			})
			.catch(() => {
				notification.error(t('PRESET_VIEWS.MANAGE_DISPLAYS.FAILED_SWITCH_TO_MERCHANT_DEFAULT_PRESET'));
			});
	}, [presetViewItems, t]);

	return (
		<Modal
			visible={visible}
			title={<ModalTitle text={t('PRESET_VIEWS.MANAGE_DISPLAYS_ACTION')} />}
			okText={t('PRESET_VIEWS.SAVE_DISPLAY')}
			maskClosable={false}
			onCancel={onClose}
			footer={footer}
			className={styles.manageDisplaysModal}
			keyboard={false}
		>
			<div className={styles.manageDisplays}>
				<div className={styles.listHeader}>
					<div className={styles.leftColumn}>{t('PRESET_VIEWS.MANAGE_DISPLAYS.NAME')}</div>
					<div className={styles.rightColumn}>{t('PRESET_VIEWS.MANAGE_DISPLAYS.ACTIONS')}</div>
				</div>
				<div className={styles.list}>
					{presetViewItems.map(presetView => (
						<ListItem
							key={`${presetView.id}-${presetView.name}`}
							isActive={presetView.id === activePreset.id}
							presetView={presetView}
							activateDefaultPresetView={activateDefaultPreset}
						/>
					))}
				</div>
			</div>
		</Modal>
	);
});
